/* eslint-disable max-nested-callbacks */
import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Select,
  Icon,
  Button,
  BUTTON_THEME,
  CheckBox,
  CircularLoader,
} from '@st-operations/uikit/src/v2'

import Header from '_components/header'
import { TabItem } from '_components/tab-item'
import PeriodSelect from '_components/period-select'
import { getCreationCount, getTop10, getCreatedByEnum } from '_services/analytics'
import LoginTimestampTable from './LoginTimestampTable'
import InsightsTable from './InsightsTable'
import { getOrganizations, getUsers, getDeactivatedUsers } from '_services/predactiv-admin'

import Collapsible from '../event-feeds/query-panel/collapsible'
import UsageGraph from './UsageGraph'
import Top10Graph from './Top10Graph'
import {
  SOURCE_VALUES,
  SOURCES_ENV,
  SOURCE_OPTIONS,
  COLLECTION_CONFIG,
  DROPDOWN_MENU_PROPS,
  isInternalEmail,
} from './constants'

const TYPE_CONFIG = {
  CREATION_COUNT: { value: 'creation-count', label: 'Total Documents' },
  LOGIN_TIMESTAMP: { value: 'login-timestamp', label: 'Login Timestamp' },
  INSIGHTS: { value: 'insights', label: 'Insights' },
}
const CREATION_COUNT_VISUALIZATION_CONFIG = {
  DATA_OVER_TIME: { value: 'data-over-time', label: 'Data Over Time' },
  TOP_10: { value: 'top-10', label: 'Top 10' },
}

const VISUALIZATION_CONFIG = {
  [TYPE_CONFIG.CREATION_COUNT.value]: CREATION_COUNT_VISUALIZATION_CONFIG,
}

const SUBTYPE_CONFIG = {
  [TYPE_CONFIG.CREATION_COUNT.value]: {
    [CREATION_COUNT_VISUALIZATION_CONFIG.DATA_OVER_TIME.value]: {
      OVERALL: {
        value: 'overall',
        label: 'Overall',
        components: {
          filter: {
            period: true,
            org: { sources: [SOURCE_VALUES.APA_DEV, SOURCE_VALUES.APA_PROD] },
          },
          group: { interval: true },
        },
      },
      BY_USERS: {
        value: 'by-users',
        label: 'By Users',
        components: {
          filter: {
            period: true,
            org: { sources: [SOURCE_VALUES.APA_DEV, SOURCE_VALUES.APA_PROD] },
          },
          group: { interval: true },
          select: { users: true },
        },
      },
      BY_ORGS: {
        value: 'by-orgs',
        label: 'By Orgs',
        sources: [SOURCE_VALUES.APA_DEV, SOURCE_VALUES.APA_PROD],
        components: {
          filter: {
            period: true,
          },
          group: { interval: true },
          select: { orgs: true },
        },
      },
    },
    [CREATION_COUNT_VISUALIZATION_CONFIG.TOP_10.value]: {
      USERS: {
        value: 'users',
        label: 'Users',
        components: {
          filter: {
            period: true,
            org: { sources: [SOURCE_VALUES.APA_DEV, SOURCE_VALUES.APA_PROD] },
          },
        },
      },
      ORGS: {
        value: 'orgs',
        label: 'Orgs',
        sources: [SOURCE_VALUES.APA_DEV, SOURCE_VALUES.APA_PROD],
        components: { filter: { period: true } },
      },
    },
  },
}

const INTERVAL_VALUES = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
}

const INTERVAL_OPTIONS = [
  { label: 'Daily', value: INTERVAL_VALUES.DAILY },
  { label: 'Weekly', value: INTERVAL_VALUES.WEEKLY },
  { label: 'Monthly', value: INTERVAL_VALUES.MONTHLY },
]

const PERIOD_TO_DATE = {
  1: {
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    endDate: new Date(),
  },
  3: {
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 3)),
    endDate: new Date(),
  },
  6: {
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 6)),
    endDate: new Date(),
  },
  12: {
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 12)),
    endDate: new Date(),
  },
}

const Analytics = () => {
  // Default to the Predactiv prod source when it's available (production
  // builds); otherwise fall back to the dev source, then the first allowed
  // source. SOURCE_OPTIONS already gates out prod in non-production builds.
  const firstSourceValue =
    SOURCE_OPTIONS.find((option) => option.value === SOURCE_VALUES.APA_PROD)?.value ||
    SOURCE_OPTIONS.find((option) => option.value === SOURCE_VALUES.APA_DEV)?.value ||
    SOURCE_OPTIONS[0].value
  const firstCollection = COLLECTION_CONFIG[firstSourceValue][0]

  const [source, setSource] = useState(firstSourceValue)
  const [collection, setCollection] = useState(firstCollection.value)

  const firstType = TYPE_CONFIG.CREATION_COUNT.value
  const firstVisualization = Object.values(VISUALIZATION_CONFIG[firstType])[0].value
  const firstSubtype =
    SUBTYPE_CONFIG[firstType]?.[firstVisualization]?.BY_ORGS?.value ||
    Object.values(SUBTYPE_CONFIG[firstType][firstVisualization])[0].value

  const [type, setType] = useState(firstType)
  const [visualization, setVisualization] = useState(firstVisualization)
  const [subtype, setSubtype] = useState(firstSubtype)

  const [groupByInterval, setGroupByInterval] = useState(INTERVAL_OPTIONS[0].value)
  const [period, setPeriod] = useState(1)
  const [orgFilterValue, setOrgFilterValue] = useState(undefined)
  const [startDate, setStartDate] = useState(undefined)
  const [endDate, setEndDate] = useState(undefined)
  const [userEmailData, setUserEmailData] = useState({ active: [], deactivated: [] })
  const activeUserEmails = userEmailData.active
  const deactivatedUserEmails = userEmailData.deactivated
  const [showDeactivatedUsers, setShowDeactivatedUsers] = useState(true)
  const [users, setUsers] = useState(undefined)
  const [orgOptions, setOrgOptions] = useState([])
  const [orgs, setOrgs] = useState(undefined)

  const [showInternalUsers, setShowInternalUsers] = useState(true)
  const [data, setData] = useState([])
  const [isReportLoading, setIsReportLoading] = useState(false)
  const hasAutoSelectedOrgsRef = useRef(false)
  const hasAutoSelectedUsersRef = useRef(false)
  // Users that unchecking "Show internal users" pulled out of the selection, so
  // re-checking the box can put back exactly what was there rather than
  // selecting every staff account in the organization.
  const internalUsersRemovedByToggleRef = useRef([])

  const isValidCollection = useMemo(
    () => COLLECTION_CONFIG[source].some((option) => option.value === collection),
    [source, collection]
  )

  const visualizationConfig = useMemo(() => VISUALIZATION_CONFIG[type] ?? {}, [type])
  const visualizationOptions = useMemo(() => Object.values(visualizationConfig), [
    visualizationConfig,
  ])
  const subtypeConfig = useMemo(() => SUBTYPE_CONFIG[type]?.[visualization] ?? {}, [
    type,
    visualization,
  ])
  const componentsConfig = useMemo(
    () => Object.values(subtypeConfig).find((config) => config.value === subtype)?.components,
    [subtypeConfig, subtype]
  )

  const isApa = useMemo(() => [SOURCE_VALUES.APA_DEV, SOURCE_VALUES.APA_PROD].includes(source), [
    source,
  ])

  const userOptions = useMemo(() => {
    const active = Array.isArray(activeUserEmails)
      ? activeUserEmails.map((email) => ({ label: email, value: email, deactivated: false }))
      : []
    const deactivated =
      showDeactivatedUsers && Array.isArray(deactivatedUserEmails)
        ? deactivatedUserEmails.map((email) => ({ label: email, value: email, deactivated: true }))
        : []
    const options = [...active, ...deactivated]
    // The checkbox has to reach the dropdown, not just the report: leaving staff
    // accounts listed here is what made it look like it did nothing.
    return showInternalUsers ? options : options.filter((option) => !isInternalEmail(option.value))
  }, [activeUserEmails, deactivatedUserEmails, showDeactivatedUsers, showInternalUsers])

  useEffect(() => {
    if (!collection || !isValidCollection) {
      setCollection(COLLECTION_CONFIG[source][0].value)
      return
    }
  }, [source, isValidCollection])

  useEffect(() => {
    const availableSubtypes = Object.values(SUBTYPE_CONFIG[type]?.[visualization] ?? {})
    if (availableSubtypes.length && !availableSubtypes.some((config) => config.value === subtype)) {
      setSubtype(availableSubtypes[0].value)
    }
  }, [type, visualization, subtype])

  useEffect(() => {
    if (!isValidCollection || !startDate || !endDate) {
      return
    }

    if (visualization === visualizationConfig?.DATA_OVER_TIME?.value) {
      const realOrgFilterValue =
        subtype !== subtypeConfig?.BY_ORGS?.value ? orgFilterValue : undefined
      const realUsers = subtype === subtypeConfig?.BY_USERS?.value ? users : undefined
      const realOrgs = subtype === subtypeConfig?.BY_ORGS?.value ? orgs : undefined

      setIsReportLoading(true)
      getCreationCount({
        source,
        groupByInterval,
        collection,
        org_filter: realOrgFilterValue,
        start_date: startDate ? startDate.toISOString().split('T')[0] : undefined,
        end_date: endDate ? endDate.toISOString().split('T')[0] : undefined,
        users: realUsers?.join(','),
        orgs: realOrgs?.map?.((option) => option.value)?.join(','),
        exclude_internal: !showInternalUsers,
      })
        .then((res) => {
          const resultMap = {}
          for (const { key, stats } of res) {
            for (const item of stats) {
              if (!resultMap[item.reference]) {
                resultMap[item.reference] = {
                  all: 0,
                  ...realUsers?.reduce((acc, user) => {
                    acc[user] = 0
                    return acc
                  }, {}),
                  ...realOrgs?.reduce((acc, org) => {
                    acc[org.value] = 0
                    return acc
                  }, {}),
                }
              }
              resultMap[item.reference][key] = item.count
            }
          }

          const initialData = Object.entries(resultMap).map(([key, value]) => ({
            reference: key,
            ...value,
          }))

          const sortedData = initialData.sort((a, b) => {
            return a.reference > b.reference ? 1 : -1
          })

          setData(sortedData)
        })
        .finally(() => {
          setIsReportLoading(false)
        })
    } else if (visualization === visualizationConfig?.TOP_10?.value) {
      setIsReportLoading(true)
      getTop10({
        source,
        collection,
        type: subtype,
        org_filter: orgFilterValue,
        start_date: startDate ? startDate.toISOString().split('T')[0] : undefined,
        end_date: endDate ? endDate.toISOString().split('T')[0] : undefined,
        exclude_internal: !showInternalUsers,
      })
        .then((res) => {
          setData(res)
        })
        .finally(() => {
          setIsReportLoading(false)
        })
    }
  }, [
    source,
    collection,
    isValidCollection,
    visualization,
    visualizationConfig,
    subtype,
    subtypeConfig,
    startDate,
    endDate,
    orgFilterValue,
    groupByInterval,
    users,
    orgs,
    showInternalUsers,
  ])

  useEffect(() => {
    if (!isValidCollection) {
      return
    }
    if (subtype !== subtypeConfig?.BY_USERS?.value) {
      return
    }

    if (isApa) {
      if (!orgFilterValue) {
        return
      }
      const mapEmails = (response) => {
        const responseList =
          response?.users ||
          response?.deactivatedUsers ||
          response?.items ||
          (Array.isArray(response) ? response : [])
        return (Array.isArray(responseList) ? responseList : [])
          .map((user) => user?.email || user)
          .filter(Boolean)
      }

      Promise.all([
        getUsers(orgFilterValue, SOURCES_ENV[source]),
        getDeactivatedUsers(orgFilterValue, SOURCES_ENV[source]),
      ])
        .then(([usersResponse, deactivatedUsersResponse]) => {
          setUserEmailData({
            active: mapEmails(usersResponse),
            deactivated: mapEmails(deactivatedUsersResponse),
          })
        })
        .catch((err) => {
          setUserEmailData({ active: null, deactivated: [] })
          console.error(err)
        })
    } else {
      getCreatedByEnum({ collection: collection?.replace('_', '-'), source })
        .then((res) => {
          setUserEmailData({ active: res || [], deactivated: [] })
        })
        .catch((err) => {
          setUserEmailData({ active: null, deactivated: [] })
          console.error(err)
        })
    }
  }, [source, collection, subtype, subtypeConfig, isApa, orgFilterValue])

  useEffect(() => {
    if (!isValidCollection) {
      return
    }
    if (![SOURCE_VALUES.APA_DEV, SOURCE_VALUES.APA_PROD].includes(source)) {
      return
    }

    getOrganizations(SOURCES_ENV[source])
      .then((res) => {
        setOrgOptions(res.items?.map((org) => ({ label: org.name, value: org._id })))
      })
      .catch((err) => {
        setOrgOptions(null)
        console.error(err)
      })
  }, [source, collection])

  useEffect(() => {
    if (period !== -1) {
      setStartDate(PERIOD_TO_DATE[period].startDate)
      setEndDate(PERIOD_TO_DATE[period].endDate)
    }
  }, [period])

  useEffect(() => {
    setUsers([])
  }, [source])

  // Auto-select all organizations for the multi-select only once per org list load
  useEffect(() => {
    if (!isApa) {
      return
    }
    if (subtype !== subtypeConfig?.BY_ORGS?.value) {
      return
    }
    if (hasAutoSelectedOrgsRef.current) {
      return
    }
    if (!Array.isArray(orgOptions) || orgOptions.length === 0) {
      return
    }
    const sortedOrgOptions = [...orgOptions]
      .sort((a, b) => (a.label > b.label ? 1 : -1))
      .slice(0, 10)
    setOrgs(sortedOrgOptions)
    hasAutoSelectedOrgsRef.current = true
  }, [isApa, orgOptions, subtype, subtypeConfig])

  // Reset the auto-select flag when source changes so a newly loaded list can auto-select once
  useEffect(() => {
    hasAutoSelectedOrgsRef.current = false
  }, [source])

  // Auto-select users for the multi-select once per organization selection (APA only)
  useEffect(() => {
    if (!isApa) {
      return
    }
    if (subtype !== subtypeConfig?.BY_USERS?.value) {
      return
    }
    if (!orgFilterValue) {
      return
    }
    if (hasAutoSelectedUsersRef.current) {
      return
    }
    const allEmails = [
      ...(Array.isArray(activeUserEmails) ? activeUserEmails : []),
      ...(showDeactivatedUsers && Array.isArray(deactivatedUserEmails)
        ? deactivatedUserEmails
        : []),
    ]
    if (allEmails.length === 0) {
      return
    }
    if (Array.isArray(users) && users.length > 0) {
      return
    }
    // Default to every external user of the organization: the report is nearly
    // always about the customer's own people, so picking them out of a list
    // padded with staff accounts was busywork. Fall back to the full list only
    // when the organization has no external users left to show.
    const externalEmails = allEmails.filter((email) => !isInternalEmail(email))
    const selection = externalEmails.length || !showInternalUsers ? externalEmails : allEmails
    setUsers([...selection].sort())
    hasAutoSelectedUsersRef.current = true
  }, [
    isApa,
    subtype,
    subtypeConfig,
    orgFilterValue,
    activeUserEmails,
    deactivatedUserEmails,
    users,
    showDeactivatedUsers,
    showInternalUsers,
  ])

  // Reset auto-select users flag when organization or source changes
  useEffect(() => {
    hasAutoSelectedUsersRef.current = false
  }, [orgFilterValue, source])

  // When Show Deactivated checkbox is toggled, add/remove deactivated users from selection
  useEffect(() => {
    if (!Array.isArray(deactivatedUserEmails) || deactivatedUserEmails.length === 0) {
      return
    }
    if (showDeactivatedUsers) {
      setUsers((prev) => {
        const prevArray = Array.isArray(prev) ? prev : []
        const toAdd = deactivatedUserEmails.filter((email) => !prevArray.includes(email))
        return [...prevArray, ...toAdd]
      })
    } else {
      setUsers((prev) => {
        if (!Array.isArray(prev)) return prev
        return prev.filter((email) => !deactivatedUserEmails.includes(email))
      })
    }
  }, [showDeactivatedUsers])

  // Dropping internal users from the dropdown is only half the job: any that
  // were already selected would otherwise stay in the chart and the CSV export.
  useEffect(() => {
    if (!showInternalUsers) {
      setUsers((prev) => {
        if (!Array.isArray(prev)) return prev
        const internal = prev.filter(isInternalEmail)
        if (!internal.length) return prev
        internalUsersRemovedByToggleRef.current = internal
        return prev.filter((email) => !isInternalEmail(email))
      })
      return
    }

    const restored = internalUsersRemovedByToggleRef.current
    internalUsersRemovedByToggleRef.current = []
    if (!restored.length) {
      return
    }
    setUsers((prev) => {
      const prevArray = Array.isArray(prev) ? prev : []
      return [...prevArray, ...restored.filter((email) => !prevArray.includes(email))]
    })
  }, [showInternalUsers])

  // Britt's ask: one click to put every external user on the report instead of
  // ticking them off one at a time.
  const selectAllExternalUsers = () => {
    setUsers(
      userOptions.filter((option) => !isInternalEmail(option.value)).map((option) => option.value)
    )
  }

  const handleExportCSV = () => {
    if (!Array.isArray(data) || data.length === 0) {
      return
    }

    let exportData

    if (visualization === visualizationConfig?.DATA_OVER_TIME?.value) {
      if (subtype === subtypeConfig?.BY_USERS?.value) {
        exportData = data.map(({ reference, all, ...userCounts }) => ({
          date: reference,
          ...userCounts,
        }))
      } else if (subtype === subtypeConfig?.BY_ORGS?.value) {
        exportData = data.map(({ reference, all, ...orgCounts }) => ({
          date: reference,
          ...Object.fromEntries(
            Object.entries(orgCounts).map(([orgId, count]) => [
              orgOptions?.find((o) => o.value === orgId)?.label || orgId,
              count,
            ])
          ),
        }))
      } else {
        exportData = data.map(({ reference, all }) => ({ date: reference, count: all }))
      }
    } else if (visualization === visualizationConfig?.TOP_10?.value) {
      if (subtype === subtypeConfig?.ORGS?.value) {
        exportData = data.map(({ email: orgId, count }) => ({
          org: orgOptions?.find((o) => o.value === orgId)?.label || orgId,
          count,
        }))
      } else {
        exportData = data.map(({ email, count }) => ({ user: email, count }))
      }
    } else {
      exportData = data
    }

    const replacer = (_, value) => (value === null || value === undefined ? '' : value)
    const header = Object.keys(exportData[0])
    let csv = exportData.map((row) =>
      header.map((fieldName) => JSON.stringify(row[fieldName], replacer)).join(',')
    )
    csv.unshift(header.join(','))
    csv = csv.join('\r\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute(
      'download',
      `analytics-${collection}-${new Date().toISOString().split('T')[0]}.csv`
    )
    link.click()
  }

  return (
    <>
      <Header title="Analytics" loading={false} />
      <div className="flex flex-col gap-1 w-full">
        <div className="flex flex-row gap-4 -m-[20px] p-[20px] mb-0 bg-gray-100">
          <Select
            className="m-0"
            label="Source"
            options={SOURCE_OPTIONS}
            value={SOURCE_OPTIONS.find((option) => option.value === source)}
            onChange={({ value }) => setSource(value)}
            {...DROPDOWN_MENU_PROPS}
            useReactSelect
          />
          <Select
            className="m-0"
            label="Collection"
            options={COLLECTION_CONFIG[source]}
            value={COLLECTION_CONFIG[source].find((option) => option.value === collection)}
            onChange={({ value }) => setCollection(value)}
            {...DROPDOWN_MENU_PROPS}
            useReactSelect
          />
          <div className="m-0 ml-2 flex items-end pt-6 gap-4">
            <CheckBox
              checked={showInternalUsers}
              onChange={(e) => setShowInternalUsers(e.target.checked)}
              label="Show internal users"
              containerStyle={{ margin: 0 }}
            />
          </div>
          {/* Login Timestamp and Insights each ship their own Export CSV over
              their own filtered rows. Leaving this one up there gave those tabs
              two download buttons, and this one still exported the creation
              count data behind them. */}
          <If condition={type === TYPE_CONFIG.CREATION_COUNT.value}>
            <div className="m-0 ml-2 flex items-end pt-6">
              <Button
                theme={BUTTON_THEME.PRIMARY}
                onClick={handleExportCSV}
                disabled={!data?.length}
                className="m-0"
              >
                <span className="inline-flex items-center gap-2">
                  <Icon
                    width="13px"
                    height="13px"
                    icon="download"
                    type="outline"
                    color="currentColor"
                  />
                  Export CSV
                </span>
              </Button>
            </div>
          </If>
        </div>
        <div className="flex flex-col gap-4 w-full">
          <div className="m-0">
            <For each="item" of={Object.values(TYPE_CONFIG)}>
              <TabItem
                key={item.value}
                onClick={() => setType(item.value)}
                isSelected={type === item.value}
                item={item.label}
              />
            </For>
          </div>
          <div className="flex flex-col gap-4">
            <Choose>
              <When condition={type === TYPE_CONFIG.CREATION_COUNT.value}>
                <div className="text-gray-500 mb-2">
                  Number of <strong className="text-gray-500 font-semibold">documents</strong>{' '}
                  created in a <strong className="text-gray-500 font-semibold">collection</strong>
                </div>
                <div className="flex flex-row gap-4 -m-[20px] p-[20px] mb-0">
                  <Select
                    className="m-0"
                    label="Visualization"
                    options={visualizationOptions}
                    value={visualizationOptions.find((option) => option.value === visualization)}
                    onChange={(option) => setVisualization(option.value)}
                    {...DROPDOWN_MENU_PROPS}
                    useReactSelect
                  />
                  <If condition={componentsConfig?.filter}>
                    <If condition={componentsConfig?.filter?.period}>
                      <PeriodSelect
                        selectClassName="w-full m-0 [&_label]:font-medium"
                        label="Period"
                        minDate={getPastThreeYears()}
                        monthFrom={startDate ? startDate.toISOString().split('T')[0] : undefined}
                        monthTo={endDate ? endDate.toISOString().split('T')[0] : undefined}
                        period={period}
                        onPeriodChange={setPeriod}
                        onMonthFromChange={(e) => setStartDate(new Date(e))}
                        onMonthToChange={(e) => setEndDate(new Date(e))}
                      />
                    </If>
                    <If
                      condition={
                        componentsConfig?.filter?.org === true ||
                        componentsConfig?.filter?.org?.sources?.includes?.(source)
                      }
                    >
                      <Select
                        className="[&_label]:font-medium w-80"
                        label="Organization"
                        placeholder="Select an organization..."
                        options={[{ label: 'All', value: undefined }, ...orgOptions]}
                        value={orgOptions.find((option) => option.value === orgFilterValue)}
                        onChange={(option) => setOrgFilterValue(option.value)}
                        {...DROPDOWN_MENU_PROPS}
                        useReactSelect
                      />
                    </If>
                  </If>
                </div>

                <div className="flex flex-row gap-4">
                  <If condition={componentsConfig?.group || componentsConfig?.select}>
                    <div className="flex flex-col gap-4 w-1/3 shrink-0">
                      <If condition={componentsConfig?.group}>
                        <Collapsible className="m-0" title="Group By" showOpenButton={false}>
                          <If condition={componentsConfig?.group?.interval}>
                            <Select
                              className="[&_label]:font-medium"
                              label="Interval"
                              options={INTERVAL_OPTIONS}
                              value={INTERVAL_OPTIONS.find(
                                (option) => option.value === groupByInterval
                              )}
                              onChange={(option) => setGroupByInterval(option.value)}
                              {...DROPDOWN_MENU_PROPS}
                              useReactSelect
                            />
                          </If>
                        </Collapsible>
                      </If>

                      <If condition={componentsConfig?.select}>
                        <Collapsible className="m-0" title="Select" showOpenButton={false}>
                          <If condition={componentsConfig?.select?.users}>
                            <Choose>
                              <When condition={activeUserEmails !== null}>
                                <Choose>
                                  <When condition={!isApa || orgFilterValue}>
                                    <div className="flex flex-row gap-3 items-center mb-1">
                                      <label className="flex items-center gap-1 text-sm cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={showDeactivatedUsers}
                                          onChange={(e) =>
                                            setShowDeactivatedUsers(e.target.checked)
                                          }
                                        />
                                        Show Deactivated
                                      </label>
                                      <button
                                        type="button"
                                        className="text-sm text-primary underline bg-transparent border-0 p-0 cursor-pointer"
                                        onClick={selectAllExternalUsers}
                                      >
                                        Select all external
                                      </button>
                                      <button
                                        type="button"
                                        className="text-sm text-gray-500 underline bg-transparent border-0 p-0 cursor-pointer"
                                        onClick={() => setUsers([])}
                                      >
                                        Clear
                                      </button>
                                    </div>
                                    <Select
                                      className="m-0 flex-1"
                                      label="Users"
                                      placeholder="Select emails..."
                                      options={userOptions}
                                      value={userOptions.filter((opt) =>
                                        users?.includes(opt.value)
                                      )}
                                      onChange={(options) => {
                                        setUsers(options?.map((option) => option.value || option))
                                      }}
                                      formatOptionLabel={(option) => (
                                        <span
                                          style={
                                            option.deactivated ? { color: '#7c3aed' } : undefined
                                          }
                                        >
                                          {option.label}
                                        </span>
                                      )}
                                      {...DROPDOWN_MENU_PROPS}
                                      useReactSelect
                                      isMulti
                                      isClearable
                                      isCreatable={true}
                                      formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                                      noOptionsMessage={() => 'Type to create new option'}
                                    />
                                  </When>
                                  <Otherwise>
                                    <div className="text-gray-500 mb-2">
                                      Choose an organization to see available users.
                                    </div>
                                  </Otherwise>
                                </Choose>
                              </When>
                              <Otherwise>
                                <span className="text-red-600">
                                  An error has occurred while fetching available users.
                                </span>
                              </Otherwise>
                            </Choose>
                          </If>
                          <If condition={componentsConfig?.select?.orgs}>
                            <Choose>
                              <When condition={orgOptions !== null}>
                                <Select
                                  className="m-0 flex-1"
                                  label="Organizations"
                                  placeholder="Select organizations..."
                                  options={orgOptions || []}
                                  value={orgs}
                                  onChange={(options) => {
                                    setOrgs(options)
                                  }}
                                  {...DROPDOWN_MENU_PROPS}
                                  useReactSelect
                                  isMulti
                                />
                              </When>
                              <Otherwise>
                                <span className="text-red-600">
                                  An error has occurred while fetching available organizations.
                                </span>
                              </Otherwise>
                            </Choose>
                          </If>
                        </Collapsible>
                      </If>
                    </div>
                  </If>

                  <div className="flex flex-col gap-4 w-2/3 grow min-w-0 border border-solid border-gray-300 rounded-[8px] px-[10px] py-2">
                    <div className="m-0 border-0 border-b border-solid border-gray-400">
                      <For each="item" of={Object.values(subtypeConfig || {})}>
                        <If condition={!item.sources?.length || item.sources.includes(source)}>
                          <TabItem
                            key={item.value}
                            onClick={() => setSubtype(item.value)}
                            isSelected={subtype === item.value}
                            item={item.label}
                          />
                        </If>
                      </For>
                    </div>
                    <div className="relative flex flex-col gap-6">
                      <If condition={isReportLoading}>
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                          <CircularLoader />
                        </div>
                      </If>
                      <Choose>
                        <When condition={!data || data?.every((item) => item.stats?.length === 0)}>
                          No data
                        </When>
                        <When
                          condition={visualization === visualizationConfig.DATA_OVER_TIME?.value}
                        >
                          <If condition={Object.keys(data || {}).length > 0}>
                            <div className="text-gray-500">
                              <If condition={subtype === subtypeConfig.OVERALL.value}>
                                {'Overall creation count over time'}
                              </If>
                              <If
                                condition={
                                  subtype === subtypeConfig.BY_USERS.value && !orgFilterValue
                                }
                              >
                                {'Please select an organization to see available users.'}
                              </If>
                              <If
                                condition={
                                  subtype === subtypeConfig.BY_USERS.value && orgFilterValue
                                }
                              >
                                {"Individual users' creation count over time"}
                              </If>
                              <If condition={subtype === subtypeConfig.BY_ORGS.value}>
                                {"Individual organizations' creation count over time'"}
                              </If>
                            </div>
                            <div className="overflow-hidden">
                              <UsageGraph
                                data={data}
                                hideAll={subtype !== subtypeConfig.OVERALL.value}
                                users={users}
                                orgs={orgs}
                              />
                            </div>
                          </If>
                        </When>
                        <When condition={visualization === visualizationConfig.TOP_10?.value}>
                          <div className="text-gray-500">{'Top 10 Users by Creation Count'}</div>
                          <div className="overflow-hidden">
                            <Top10Graph data={data} />
                          </div>
                        </When>
                      </Choose>
                    </div>
                  </div>
                </div>
              </When>
              <When condition={type === TYPE_CONFIG.LOGIN_TIMESTAMP.value}>
                <div className="text-gray-500 mb-2">List of users login by time</div>
                <LoginTimestampTable orgOptions={orgOptions} source={source} />
              </When>
              <When condition={type === TYPE_CONFIG.INSIGHTS.value}>
                <div className="text-gray-500 mb-2">List of recorded insights events</div>
                <InsightsTable
                  orgOptions={orgOptions}
                  source={source}
                  showInternalUsers={showInternalUsers}
                />
              </When>
            </Choose>
          </div>
        </div>
      </div>
    </>
  )
}

const getPastThreeYears = () => {
  const date = new Date()

  const year = date.getFullYear()

  const threeYearsAgo = new Date(date)
  threeYearsAgo.setFullYear(year - 3)
  const threeYearsAgoYear = threeYearsAgo.getFullYear()
  const threeYearsAgoMonth = String(threeYearsAgo.getMonth() + 1).padStart(2, '0')

  return `${threeYearsAgoYear}-${threeYearsAgoMonth}`
}

export default Analytics
